/**
 * Helper (pure) ของการค้น + แบ่งหน้า run ที่ **ฝั่ง server** (`GET /runs`)
 *
 * ไฟล์นี้แค่ประกอบ query params แล้วให้หลังบ้านค้นจาก run ทั้งหมด ⇒ เจอ run ที่อยู่นอกกองที่โหลดมาแล้ว
 * ใช้ร่วมกันทั้งแท็บ Videos (ทั้งโปรเจกต์) และแผง episode ของแท็บ Playlist (เฉพาะ series ที่เลือก)
 * — ต่างกันแค่ `RunsFilter.seriesId` กับ slice ปลายทางใน MeGenSequence (`RunsSliceKey`)
 */
import { RunData } from "../types/MeGenTypes";

/** เพดานจริงของ backend — ขอเกินนี้ถูก **ตัดเงียบ** ไม่ error */
export const RUNS_MAX_LIMIT = 200;

/** จำนวนแถวต่อการโหลด 1 ครั้ง (กด "โหลดเพิ่ม" 1 ที = ได้อีกเท่านี้) */
export const RUNS_PAGE_SIZE = 50;

/** หน่วงก่อนยิงค้นหา — ให้ตรงกับช่องกรอกราคาใน AiRatePriceCell */
export const RUNS_SEARCH_DEBOUNCE_MS = 350;

export type RunSortOrder = "newest" | "oldest";

export type RunsFilter = {
  q: string;
  sort: RunSortOrder;
  /** จำกัดเฉพาะ episode ของ series เดียว (แผง Playlist) · ไม่ส่ง/null = ทั้งโปรเจกต์ (แท็บ Videos) */
  seriesId?: number | null;
};

export type RunsSlice = RunsFilter & {
  items: RunData[];
  /** จำนวนทั้งหมดหลังกรอง (ไม่ใช่จำนวนใน items) — ใช้กับ badge */
  total: number;
  hasMore: boolean;
  loading: boolean;
  /** ลายเซ็นคำขอที่กำลังบิน — response ที่ key ไม่ตรงถือว่าเก่า ต้องทิ้ง */
  requestKey: string;
};

/** key ใน MeGenSequence ที่เก็บ RunsSlice — 1 หน้าจอ = 1 slice ค้น/แบ่งหน้าแยกกัน (ผลไม่ปนกัน) */
export type RunsSliceKey = "runsVideos" | "runsPlaylist";

export const RUNS_FILTER_INITIAL: RunsFilter = { q: "", sort: "newest" };

export const RUNS_SLICE_INITIAL: RunsSlice = {
  ...RUNS_FILTER_INITIAL,
  items: [],
  total: 0,
  hasMore: false,
  loading: false,
  requestKey: "",
};

export type RunsRequest = {
  projectId: number;
  filter: RunsFilter;
  offset: number;
  limit: number;
};

const clampLimit = (n: number): number =>
  Math.min(RUNS_MAX_LIMIT, Math.max(1, Math.floor(n) || 1));

/**
 * ตอนมีคำค้นไม่ต้องรอ hydrate — เร็วกว่าราว 3 เท่า เพราะข้ามการเปิด LangGraph state
 * แลกกับ run ที่ยังทำงานอยู่จะได้ `status: "in_progress"` แทนชื่อ node ที่ค้างอยู่
 * (`EpisodeItem` แยกสีแค่ completed/error/stopped ที่เหลือตกสีเดียวกัน ⇒ สีไม่เพี้ยน)
 */
const hydrateFor = (filter: RunsFilter): 0 | 1 => (filter.q.trim() ? 0 : 1);

export const buildRunsParams = (req: RunsRequest): Record<string, string | number> => {
  const q = req.filter.q.trim();
  const hydrate = hydrateFor(req.filter);

  return {
    project_id: req.projectId,
    // แผง Playlist: ให้ server กรอง series เอง
    // (ไม่งั้นต้องโหลดทั้งโปรเจกต์มากรองในเบราว์เซอร์ ซึ่งชนเพดาน 200)
    ...(req.filter.seriesId != null ? { series_id: req.filter.seriesId } : {}),
    limit: clampLimit(req.limit),
    offset: Math.max(0, req.offset),
    // ส่ง sort เสมอ — ถ้าไม่ส่งตอนมี q หลังบ้านจะเรียงแบบ relevance
    // ทำให้ปุ่มสลับ newest/oldest ดูเหมือนกดไม่ติด
    sort: req.filter.sort === "newest" ? "created_at:desc" : "created_at:asc",
    ...(q ? { q } : {}),
    ...(hydrate === 0 ? { hydrate } : {}),
  };
};

/** params ชุดเดียวกัน = ผลลัพธ์ชุดเดียวกัน ⇒ ใช้เทียบว่า response ที่กลับมายังตรงกับที่รออยู่ไหม */
export const runsRequestKey = (req: RunsRequest): string => JSON.stringify(buildRunsParams(req));

/**
 * ดึงเฉพาะฟิลด์ตัวกรองออกจาก slice — ใช้ยิงซ้ำด้วยตัวกรองเดิม (refresh หลังลบ/duplicate)
 * โดยไม่พ่วง items/loading/requestKey · ไม่มี series ก็ไม่ใส่ key (params ของแท็บ Videos เหมือนเดิมเป๊ะ)
 */
export const sliceFilter = (slice: RunsSlice): RunsFilter => ({
  q: slice.q,
  sort: slice.sort,
  ...(slice.seriesId != null ? { seriesId: slice.seriesId } : {}),
});

/**
 * โหลดซ้ำหลังลบ/duplicate run — ขอเท่าที่ผู้ใช้กด "โหลดเพิ่ม" สะสมไว้ ใน request เดียว
 * ⇒ กลับมาแล้วเห็นความลึกเท่าเดิม ไม่เด้งกลับไปหน้าแรก และไม่ต้องจำเลขหน้า
 */
export const refetchLimit = (loadedCount: number): number =>
  clampLimit(Math.max(RUNS_PAGE_SIZE, loadedCount));

/** รวม run ที่เพิ่งโหลดเข้าแคช id→run (ไม่เคยล้าง) — ใช้หาชื่อ run ที่ไม่อยู่ในผลค้นหาปัจจุบัน */
export const mergeRunIndex = (
  index: Record<string, RunData>,
  runs: RunData[],
): Record<string, RunData> => {
  if (!runs.length) return index;
  const next = { ...index };
  runs.forEach((run) => {
    next[run.id] = run;
  });
  return next;
};

/**
 * ต่อหน้าถัดไปท้ายลิสต์เดิม โดยตัด id ซ้ำทิ้ง
 * (offset paging: ถ้ามี run ใหม่ถูกสร้างระหว่างที่ผู้ใช้กดโหลดเพิ่ม แถวเดิมจะเลื่อนมาซ้ำได้)
 */
export const appendRuns = (prev: RunData[], incoming: RunData[]): RunData[] => {
  const seen = new Set(prev.map((r) => r.id));
  return [...prev, ...incoming.filter((r) => !seen.has(r.id))];
};
